import { ICoreContext, ITimeFormatOptions } from '@msdyn365-commerce/core';
import { LOCALIZATION_DEFAULT_LOCALE, LOCALIZATION_DEFAULT_TIMEZONE } from '../configuration/localization';
import { ILocalizationState } from '../states/localization-state';

/**
 * Formats time.
 * @param context - Core context.
 * @param time - Time to display.
 * @returns String value.
 */
// Example usage: formatTime(this.props.context, new Date(CreatedTime)

export const formatTime = (context: ICoreContext, time: Date | undefined) => {
    if (time === undefined) {
        return '';
    }

    const timeOptions: ITimeFormatOptions = { hour: 'numeric', minute: 'numeric', second: 'numeric', timeZone: 'UTC' };
    return context.cultureFormatter.formatDate(time, timeOptions);
};

/**
 * Returns the UTC offset for a given date and time zone as a formatted string.
 *
 * The offset is calculated by comparing the provided date in the specified time zone
 * to the same date in UTC. The result is formatted as `UTC+X` or `UTC-X`, where `X`
 * is the absolute number of hours offset from UTC.
 *
 * @param date - The date for which to calculate the offset.
 * @param timeZone - The IANA time zone identifier (e.g., 'Europe/Berlin').
 * @returns The formatted UTC offset string (e.g., 'UTC+2').
 */
export const getOffsetFormatted = (date: Date, timeZone: string): string => {
    const utcDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));

    const tzDate = new Date(date.toLocaleString('en-US', { timeZone }));

    const diffMs = tzDate.getTime() - utcDate.getTime();
    const diffHours = diffMs / (1000 * 60 * 60);

    const sign = diffHours >= 0 ? '+' : '-';
    const absHours = Math.abs(diffHours);

    return `UTC${sign}${absHours}`;
};

/**
 * Formats an event's date and time based on the provided UTC timestamp and localization state.
 *
 * @param utcTimestamp - The UTC timestamp of the event.
 * @param localizationState - The localization state containing timezone and locale information.
 * @returns An object containing formatted date, time, and UTC offset.
 */
export const formatEventDateTime = (utcTimestamp: string, localizationState: ILocalizationState) => {
    const timeZone = localizationState.timeZone || LOCALIZATION_DEFAULT_TIMEZONE;
    const locale = localizationState.dateLocale || LOCALIZATION_DEFAULT_LOCALE;

    const date = new Date(utcTimestamp);

    const formattedDate = new Intl.DateTimeFormat(locale, {
        dateStyle: 'medium',
        timeZone
    }).format(date);

    const formattedTime = new Intl.DateTimeFormat(locale, {
        timeStyle: 'short',
        timeZone
    }).format(date);

    const offset = getOffsetFormatted(date, timeZone);

    return { formattedDate, formattedTime, offset };
};
